'use client';

/**
 * AuthContext.tsx — React Context for autentiserings-state
 *
 * Holder auth-state (user, loading) og eksponerer login/logout-funksjoner.
 * Wrappes rundt hele appen i src/app/layout.tsx.
 *
 * Separation of concerns:
 * - src/lib/api.ts — HTTP-kall (login)
 * - src/lib/auth.ts — token-lagring, JWT-decode, getCurrentUser
 * - src/contexts/AuthContext.tsx (denne) — React-state + login/logout
 * - src/hooks/useAuth.ts — hook som konsumerer context
 *
 * State:
 * 1. user — innlogget bruker (AuthUser | null)
 * 2. loading — true under initialisering (leser token fra storage)
 *
 * Funksjoner:
 * 1. login — kaller api.login(), lagrer token, oppdaterer user-state
 * 2. logout — fjerner token, setter user=null
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { login as apiLogin } from '@/lib/api';
import {
  clearToken,
  getCurrentUser,
  getTokenExpiryMs,
  storeToken,
} from '@/lib/auth';
import {
  openUserStreamFromStorage,
  closeUserStream,
} from '@/lib/user-stream';
import type { AuthUser } from '@/types/auth';
import UserStreamListener from '@/components/UserStreamListener';

/**
 * Context-verdi — det useAuth() returnerer.
 */
interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string, remember: boolean) => Promise<void>;
  logout: () => void;
  /** true mens innloggingsanimasjonen spilles: ClientUiShell venter med redirect fra /login */
  holdRedirect: boolean;
  setHoldRedirect: (hold: boolean) => void;
  /** Innloggingsanimasjonen. Eies her (ikke i LoginSection) fordi den må overleve navigasjonen til /dashboard. */
  loginTransition: LoginTransitionState | null;
  setLoginTransition: (state: LoginTransitionState | null) => void;
}

export interface LoginTransitionState {
  phase: 'running' | 'done' | 'failed' | 'leaving';
  name: string;
}

// Opprett context med undefined som default — kaster feil hvis brukt uten provider
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * SESSION_EXPIRY_LEAD_MS — hvor mange millisekunder FØR JWT utløper
 * brukeren skal logges ut og sendes til /session-expired.
 *
 * 30 s før: gir brukeren en rettferdig margin — klokken i nettleseren
 * kan avvike noen sekunder fra serveren, og et API-kall som lander ETTER
 * exp ville gitt en kryptisk 401 i stedet for en tydelig beskjed.
 */
const SESSION_EXPIRY_LEAD_MS = 30_000;

/**
 * AuthProvider — wrapper som gir auth-state til barn
 *
 * På mount: les token fra storage, sett user via getCurrentUser().
 * Dette gjør at innlogging bevares ved page refresh.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [holdRedirect, setHoldRedirect] = useState(false);
  const [loginTransition, setLoginTransition] = useState<LoginTransitionState | null>(null);

  // 1. Initialisering — les token fra storage ved mount
  // Leser fra localStorage (eksternt system) som kun er tilgjengelig på client.
  // Initial-state (null/true) matcher SSR-render, så ingen hydration mismatch.
  /* eslint-disable react-hooks/set-state-in-effect -- legitim: synkroniserer med localStorage etter mount */
  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
    setLoading(false);
    // Hvis brukeren allerede er autentisert ved mount (refresh / direkte
    // navigasjon), åpne SSE-strømmen. Strømmen er per-bruker og lever
    // så lenge brukeren er logget inn.
    if (currentUser) {
      openUserStreamFromStorage();
    }
    // Cleanup: lukk strømmen når AuthProvider unmountes (app shutdown)
    return () => {
      closeUserStream();
    };
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  // 2. login — autentiserer mot backend og oppdaterer state
  const login = useCallback(
    async (username: string, password: string, remember: boolean): Promise<void> => {
      const response = await apiLogin({ username, password });
      storeToken(response.token, remember);
      setUser(getCurrentUser());
      // Åpne SSE-strøm etter vellykket login — strømmen er per-bruker
      openUserStreamFromStorage();
    },
    []
  );

  // 3. logout — fjerner token, lukker SSE-strøm og nullstiller state
  const logout = useCallback(() => {
    closeUserStream();
    clearToken();
    setUser(null);
  }, []);

  // 4. Session-expiry-timer — planlegger automatisk utlogging NÅR
  // JWT-nøkkelen expires. Kjører ved mount (refresh/direkte navigasjon)
  // og etter hver login. Sender brukeren til /session-expired med en
  // tydelig beskjed i stedet for å la API-kall feile i det stille.
  useEffect(() => {
    if (!user) return; // ikke innlogget — ingen timer

    const expiryMs = getTokenExpiryMs();
    if (expiryMs === null) return; // ugyldig token — la guardene ta det

    const msUntilExpiry = expiryMs - Date.now() - SESSION_EXPIRY_LEAD_MS;

    // Allerede utløpt (eller under lead-margin) → logg ut umiddelbart
    if (msUntilExpiry <= 0) {
      logout();
      window.location.href = '/session-expired';
      return;
    }

    const timer = setTimeout(() => {
      logout();
      window.location.href = '/session-expired';
    }, msUntilExpiry);

    return () => clearTimeout(timer);
  }, [user, logout]);

  const value: AuthContextValue = { user, loading, login, logout, holdRedirect, setHoldRedirect, loginTransition, setLoginTransition };

  return (
    <AuthContext.Provider value={value}>
      {/* UserStreamListener: lytter på SSE-events og viser Toast.
          Rendrer null — ingen UI, kun side-effect. Plassert her slik at
          den kun er aktiv når AuthProvider er mountet (dvs. hele appen). */}
      <UserStreamListener />
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuthContext — hook for å konsumere auth-context
 *
 * Kaster feil hvis brukt utenfor AuthProvider.
 * Eksporteres direkte herfra, men src/hooks/useAuth.ts er den offisielle
 * offentlige API-en for komponenter.
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext må brukes innenfor en AuthProvider');
  }
  return context;
}
